import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { SwitchTransition } from 'react-transition-group';
import SplashScreen from './SplashScreen'
import AudioRecorder from '../AudioRecorder';
import { t } from '../../../../utilities/translator';
import useAIAssistant from '../useAIAssistant';
import WizardSelectorV2 from './WizardSelectorV2';
import FadeTransition from '../../../../components/animations/FadeTransition';
import { setStepName, setWizardType } from '../../onboardingSlice';

type Props = {
  onSkip?: () => void,
}

const AIAssistantV2 = (props: Props) => {
  const {
    onSkip,
  } = props;
  const dispatch = useDispatch();
  const [showPanel, setShowPanel] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [showSelector, setShowSelector] = useState(localStorage.getItem('wasAIAssistantShown') === 'true');

  const {
    prompt,
    isRecording,
    setIsRecording,
    handleSetPrompt,
    handleSendAudio,
    handleStartConversationClick,
    handleFeedBuilderClick,
    isAudioLoading,
    error,
    setError,
    MAX_PROMPT_LENGTH,
  } = useAIAssistant();

  useEffect(() => {
    setTimeout(() => {
      setShowPanel(true);
    }, 4000);
  }, [])

  const onShowAssistantClick = () => {
    setShowSelector(false);
  }

  const onShowFeedBuilderClick = () => {
    dispatch(setWizardType('wizard'));
    dispatch(setStepName('who'));
  }

  const onGenerateFeedClick = () => {
    dispatch(setWizardType('assistant'));
    dispatch(setStepName('loading-ai'));
    handleStartConversationClick();
  }

  const startBuilder = () => {
    dispatch(setWizardType('wizard'));
    dispatch(setStepName('who'));
    handleFeedBuilderClick();
  }

  return (
    <div className="assistant-v2">
      <FadeTransition in={!showPanel} unmountOnExit>
        <SplashScreen />
      </FadeTransition>
      {showPanel && (
        <div className={cn('assistant-v2__panel slide-in-bottom-v2')}>
          <SwitchTransition mode='out-in'>
            <FadeTransition key={showSelector}>
              <div>
                {showSelector && (
                  <WizardSelectorV2
                    onShowAssistantClick={onShowAssistantClick}
                    onShowFeedBuilderClick={onShowFeedBuilderClick}
                  />
                )}
                {!showSelector && (
                  <div className="flex flex-col gap-5">
                    <div className="skip-btn" onClick={onSkip}>{t('onboarding.skip')}</div>
                    <div className="flex gap-3">
                      <span className="text-xl font-semibold text-start">{t('onboarding.assistant.title')}</span>
                      <i className="ri-bard-line text-2xl" />
                    </div>
                    <div className="relative h-full">
                      {!isRecording && (
                        <>
                          <textarea
                            name="prompt"
                            placeholder={t('onboarding.assistant.placeholder')}
                            className={cn('ai-textarea', { loading: isAudioLoading, 'h-full': isFocus })}
                            value={prompt}
                            onChange={(e) => handleSetPrompt(e.target.value)}
                            maxLength={MAX_PROMPT_LENGTH}
                            onFocus={() => setIsFocus(true)}
                            onBlur={() => setIsFocus(false)}
                          />
                          <div className="prompt-length">
                            <span>{prompt.length}/{MAX_PROMPT_LENGTH}</span>
                          </div>
                        </>
                      )}
                      <AudioRecorder
                        isRecording={isRecording}
                        setIsRecording={setIsRecording}
                        onFinishRecording={handleSendAudio}
                        showLoader={isAudioLoading}
                        setError={setError}
                      />
                    </div>
                    {error && (
                      <div className="ai-error-message">{t('onboarding.assistant.error')}</div>
                    )}
                    <button
                      className="start-ai-search w-full"
                      onClick={onGenerateFeedClick}
                      disabled={prompt.length < 3 || isAudioLoading || isRecording}
                    >
                      <span>{t('onboarding.assistant.generateFeedBtn')}</span>
                    </button>
                    <div className="flex flex-col gap-5 items-center">
                      <button className="feed-builder-btn text-tertiary font-semibold" onClick={startBuilder}>{t('onboarding.assistant.feedBuilderBtn')}</button>
                    </div>
                  </div>
                )}
              </div>
            </FadeTransition>
          </SwitchTransition>
        </div>
      )}
    </div>
  )
}

export default AIAssistantV2
